import React, { useEffect, useState } from "react";

type AnimatedResultAmountProps = {
  value: number;
  delayMs?: number;
  durationMs?: number;
};

const DEFAULT_DELAY_MS = 160;
const DEFAULT_DURATION_MS = 850;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const amountFormatter = new Intl.NumberFormat("ja-JP", { maximumFractionDigits: 0 });

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches;

export const AnimatedResultAmount = ({
  value,
  delayMs = DEFAULT_DELAY_MS,
  durationMs = DEFAULT_DURATION_MS,
}: AnimatedResultAmountProps) => {
  const target = Number.isFinite(value) ? Math.round(value) || 0 : 0;
  const delay = Number.isFinite(delayMs) ? Math.max(0, delayMs) : DEFAULT_DELAY_MS;
  const duration = Number.isFinite(durationMs) ? Math.max(0, durationMs) : DEFAULT_DURATION_MS;
  const [displayedAmount, setDisplayedAmount] = useState(() => (prefersReducedMotion() ? target : 0));
  const finalAmount = amountFormatter.format(target);

  useEffect(() => {
    const media = typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION_QUERY) : undefined;
    let frameId: number | undefined;
    let stopped = false;
    const cancelFrame = () => {
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
        frameId = undefined;
      }
    };
    const finish = () => {
      stopped = true;
      cancelFrame();
      setDisplayedAmount(target);
    };
    const onMotionChange = () => {
      if (media?.matches) finish();
    };

    if (media?.addEventListener) media.addEventListener("change", onMotionChange);
    else media?.addListener(onMotionChange);

    if (media?.matches || duration === 0 || target === 0 || typeof window.requestAnimationFrame !== "function") {
      finish();
    } else {
      setDisplayedAmount(0);
      const startedAt = performance.now();
      const tick = (timestamp: number) => {
        if (stopped) return;
        const progress = Math.min(1, Math.max(0, (timestamp - startedAt - delay) / duration));
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        setDisplayedAmount(Math.round(target * easedProgress) || 0);
        if (progress === 1) {
          stopped = true;
          frameId = undefined;
        } else {
          frameId = window.requestAnimationFrame(tick);
        }
      };
      frameId = window.requestAnimationFrame(tick);
    }

    return () => {
      stopped = true;
      cancelFrame();
      if (media?.removeEventListener) media.removeEventListener("change", onMotionChange);
      else media?.removeListener(onMotionChange);
    };
  }, [target, delay, duration]);

  return (
    <span
      style={{ display: "inline-grid", position: "relative", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
    >
      <span aria-hidden="true" style={{ gridArea: "1 / 1", visibility: "hidden" }}>
        {finalAmount}
      </span>
      <span aria-hidden="true" data-testid="diagnosis-amount-value" style={{ gridArea: "1 / 1", textAlign: "right" }}>
        {amountFormatter.format(displayedAmount)}
      </span>
      <span
        data-testid="diagnosis-amount-accessible"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {finalAmount}
      </span>
    </span>
  );
};
